import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  AlertTriangle,
  RotateCcw,
  PauseCircle,
  ArrowRight,
  Coins,
} from "lucide-react";
import { cn } from "@/utils/cn";

interface LimitReachedCardProps {
  reason: string;
  numTurns?: number;
  percentUsed?: number;
  resetAt?: string;
  /** Server-decided: the session is alive and can be picked up with its context. */
  resumable?: boolean;
  resumesLeft?: number;
  onContinue?: () => void;
  onNewConversation: () => void;
}

function formatResetDate(iso?: string, locale = "fr-FR"): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return "";
  }
}

export const LimitReachedCard: React.FC<LimitReachedCardProps> = ({
  reason,
  numTurns,
  percentUsed,
  resetAt,
  resumable = false,
  resumesLeft,
  onContinue,
  onNewConversation,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language === "en" ? "en-GB" : "fr-FR";
  const resetLabel = formatResetDate(resetAt, locale);
  const pct = percentUsed ?? 100;
  const isQuotaExceeded = reason === "quota_exceeded";
  const canContinue = resumable && Boolean(onContinue);

  let title: string;
  let description: string;

  if (reason === "quota_exceeded") {
    title = t("aiAgent.limit.quotaExceededTitle");
    description = resetLabel
      ? t("aiAgent.limit.quotaExceededDescWithReset", { pct, date: resetLabel })
      : t("aiAgent.limit.quotaExceededDesc", { pct });
  } else if (reason === "error_max_budget_usd") {
    title = t("aiAgent.limit.maxBudgetTitle");
    description = canContinue
      ? t("aiAgent.limit.maxBudgetDescResumable")
      : t("aiAgent.limit.maxBudgetDesc");
  } else if (reason === "max_resumes") {
    title = t("aiAgent.limit.maxResumesTitle");
    description = t("aiAgent.limit.maxResumesDesc");
  } else if (reason === "session_expired") {
    title = t("aiAgent.limit.sessionExpiredTitle");
    description = t("aiAgent.limit.sessionExpiredDesc");
  } else if (reason === "error_max_turns") {
    title = t("aiAgent.limit.maxTurnsTitle");
    description = canContinue
      ? numTurns != null
        ? t("aiAgent.limit.maxTurnsDescResumable", { turns: numTurns })
        : t("aiAgent.limit.maxTurnsDescResumableNoCount")
      : numTurns != null
        ? t("aiAgent.limit.maxTurnsDesc", { turns: numTurns })
        : t("aiAgent.limit.maxTurnsDescNoCount");
  } else {
    title = t("aiAgent.limit.interruptedTitle");
    description = t("aiAgent.limit.interruptedDesc");
  }

  // A resumable stop is a pause, not a failure — amber alarm styling would
  // overstate it. Neutral/primary reads as "your turn" instead of "something
  // broke", which is what actually happened.
  const Icon = canContinue ? PauseCircle : AlertTriangle;

  return (
    <div
      role={canContinue ? "status" : "alert"}
      aria-live="polite"
      className={cn(
        "mx-4 mb-3 rounded-lg border transition-colors duration-200",
        canContinue
          ? "border-primary/25 bg-primary/5"
          : "border-amber-200 bg-amber-50 dark:border-amber-900/40 dark:bg-amber-950/30",
      )}
    >
      <div className="flex gap-3 p-3.5">
        <div
          className={cn(
            "shrink-0 mt-0.5 flex items-center justify-center h-8 w-8 rounded-lg",
            canContinue
              ? "bg-primary/10"
              : "bg-amber-100 dark:bg-amber-900/40",
          )}
        >
          <Icon
            className={cn(
              "h-4 w-4",
              canContinue
                ? "text-primary"
                : "text-amber-700 dark:text-amber-400",
            )}
            aria-hidden="true"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              "text-sm font-semibold leading-tight",
              canContinue
                ? "text-foreground"
                : "text-amber-900 dark:text-amber-100",
            )}
          >
            {title}
          </p>
          <p
            className={cn(
              "text-sm mt-1 leading-relaxed",
              canContinue
                ? "text-muted-foreground"
                : "text-amber-800/90 dark:text-amber-200/80",
            )}
          >
            {description}
          </p>

          {/* Only surface the remaining-resume count when it is close to running
              out — showing "3 left" up front invites people to spend them. */}
          {canContinue && resumesLeft != null && resumesLeft <= 1 && (
            <p className="text-xs text-muted-foreground/80 mt-1.5">
              {t("aiAgent.limit.resumesLeft", { count: resumesLeft })}
            </p>
          )}

          {/* Quota épuisé (mensuel + crédits) : proposer l'achat de crédits —
              débloque le chat immédiatement, sans attendre le reset mensuel. */}
          {isQuotaExceeded && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                onClick={() => navigate("/dashboard/subscription")}
                className="cursor-pointer h-8 gap-1.5 bg-amber-600 hover:bg-amber-700 text-white border-0 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
              >
                <Coins className="h-3.5 w-3.5" aria-hidden="true" />
                {t("aiCredits.buyCredits")}
              </Button>
            </div>
          )}

          {!isQuotaExceeded && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {canContinue && (
                <Button
                  type="button"
                  size="sm"
                  onClick={onContinue}
                  className="cursor-pointer h-8 gap-1.5 transition-colors duration-200"
                >
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("aiAgent.limit.continue")}
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                variant={canContinue ? "outline" : "default"}
                onClick={onNewConversation}
                className={cn(
                  "cursor-pointer h-8 gap-1.5 transition-colors duration-200",
                  !canContinue &&
                    "bg-amber-600 hover:bg-amber-700 text-white border-0 focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2",
                )}
              >
                <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                {t("aiAgent.limit.newConversation")}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
